import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@artosera/ui';
import { TYPES_PARTAGE_ESPACE } from '@artosera/shared';
import {
  espaceCollectionneurApi,
  useActionEspace,
  usePropositionsEspace,
} from '../../api/espaceCollectionneur.js';
import { estApiError } from '../../api/errors.js';
import { useShellContext } from '../shell/context.js';
import styles from './EspaceCollectionneur.module.css';

export function PartagerDialog({
  contactId,
  onFermer,
}: {
  contactId: string;
  onFermer: () => void;
}) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const id = useId();
  const propositions = usePropositionsEspace(galerieSlug, contactId);
  const [selection, setSelection] = useState<number[]>([]);
  const [prevenir, setPrevenir] = useState(false);
  const partage = useActionEspace(
    galerieSlug,
    (corps: Parameters<typeof espaceCollectionneurApi.partager>[2], appel) =>
      espaceCollectionneurApi.partager(galerieSlug, contactId, corps, appel),
  );
  const details = estApiError(partage.error)
    ? (partage.error.details as { raison?: string; index?: number } | null)
    : null;
  const indexRefuse =
    details?.raison === 'element_non_partageable' && details.index !== undefined
      ? selection[details.index]
      : undefined;
  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o) onFermer();
      }}
      title={t('espaceCollectionneur.partager')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          const elements = selection
            .map((i) => propositions.data!.propositions[i]!)
            .map(({ type, cibleId, exemplaireId }) => ({
              type,
              cibleId,
              ...(exemplaireId ? { exemplaireId } : {}),
            }));
          partage.mutate({ elements, prevenir }, { onSuccess: onFermer });
        }}
      >
        {propositions.isLoading && <p role="status">{t('commun:chargement')}</p>}
        {propositions.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
        {propositions.data?.propositions.length === 0 && (
          <p>{t('espaceCollectionneur.aucuneProposition')}</p>
        )}
        {TYPES_PARTAGE_ESPACE.map((type) => {
          const groupe =
            propositions.data?.propositions
              .map((p, index) => ({ ...p, index }))
              .filter((p) => p.type === type) ?? [];
          return groupe.length ? (
            <fieldset key={type}>
              <legend>{t(`espaceCollectionneur.types.${type}`)}</legend>
              <ul className={styles.liste}>
                {groupe.map((p) => (
                  <li key={p.index}>
                    <label>
                      <input
                        type="checkbox"
                        checked={p.dejaPartage || selection.includes(p.index)}
                        disabled={p.dejaPartage || partage.isPending}
                        aria-invalid={indexRefuse === p.index || undefined}
                        aria-describedby={indexRefuse === p.index ? `${id}-erreur` : undefined}
                        onChange={(e) => {
                          partage.reset();
                          setSelection(
                            e.target.checked
                              ? [...selection, p.index]
                              : selection.filter((i) => i !== p.index),
                          );
                        }}
                      />{' '}
                      {p.libelle}
                      {p.dejaPartage && ` — ${t('espaceCollectionneur.dejaPartage')}`}
                    </label>
                    {indexRefuse === p.index && (
                      <p id={`${id}-erreur`} role="alert">
                        {t('espaceCollectionneur.elementNonPartageable')}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </fieldset>
          ) : null;
        })}
        <label>
          <input
            type="checkbox"
            checked={prevenir}
            onChange={(e) => setPrevenir(e.target.checked)}
          />{' '}
          {t('espaceCollectionneur.prevenir')}
        </label>
        <Button
          type="submit"
          disabled={!selection.length || selection.length > 100}
          loading={partage.isPending}
        >
          {t('espaceCollectionneur.partager')}
        </Button>
        {partage.isError && indexRefuse === undefined && (
          <p role="alert">{t('commun:erreurReseau')}</p>
        )}
      </form>
    </Dialog>
  );
}
